<template>
    <div class="step -mx-2 px-2">
        <!-- Program search -->
        <div class="relative mb-2">
            <UiInput
                id="program-search"
                :input-value="programSearchQuery"
                @update:input-value="$emit('update:programSearchQuery', $event)"
                placeholder="Search program"
                :options="{
                    icon: 'search',
                    iconPosition: 'left',
                    hideLabel: true,
                }"
                label="Search program"
            />
        </div>

        <!-- List of programs -->
        <div
            class="programs-list overflow-y-auto pr-2 tooltip-hidden -mx-2 px-2 pt-1"
            style="scrollbar-width: thin; height: 405px; position: relative"
        >
            <div
                v-if="filteredProgramChoices.length === 0 && programSearchQuery !== ''"
                class="flex flex-col justify-center items-center gap-2"
                style="
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 100%;
                "
            >
                <div class="flex w-6 h-6 justify-center items-center">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-6 h-6 text-slate-800 dark:text-slate-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="1.5"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>
                <p
                    class="text-slate-500 dark:text-slate-400 text-center font-['Inter'] text-sm font-normal leading-5"
                >
                    No results found
                </p>
            </div>
            <div
                v-else-if="!canChooseProgramChoice"
                class="text-center py-8 text-slate-500 dark:text-slate-400"
            >
                Sorry, there are no available programs. You might have already
                added these.
            </div>
            <template v-else>
                <label
                    v-for="(programChoice, index) in filteredProgramChoices"
                    :key="'program-choice-' + programChoice.id"
                    class="block relative cursor-pointer tooltip-fix rounded-lg transition-colors focus-within:ring-2 focus-within:ring-slate-300 focus-within:ring-offset-0"
                    :class="{
                        'opacity-50 cursor-not-allowed': !programChoice.active,
                        'hover:bg-slate-50 dark:hover:bg-slate-700/50': programChoice.active,
                    }"
                    :tabindex="programChoice.active ? 0 : -1"
                    @keydown.enter.prevent="
                        programChoice.active &&
                            $emit('update:program-id', programChoice.id)
                    "
                    @keydown.space.prevent="
                        programChoice.active &&
                            $emit('update:program-id', programChoice.id)
                    "
                >
                    <div
                        class="flex py-4 px-2 items-center gap-4 self-stretch relative rounded-lg"
                        :class="{
                            'bg-slate-50 dark:bg-slate-700/50':
                                program.program_id === programChoice.id,
                        }"
                        :data-program-active="
                            programChoice.active ? 'true' : 'false'
                        "
                    >
                        <div
                            class="flex items-center justify-center rounded-lg border border-slate-200 dark:border-slate-700"
                            style="width: 112px; height: 48px; padding: 6px 14px"
                        >
                            <img
                                v-if="programChoice.image"
                                :src="getProgramImageUrl(programChoice.image)"
                                :alt="programChoice.name"
                                class="max-w-full max-h-full object-contain"
                            />
                            <span
                                v-else
                                class="text-[10px] font-medium text-gray-500 dark:text-slate-400 text-center"
                            >
                                {{ programChoice.name?.slice(0, 10) ?? '' }}
                            </span>
                        </div>
                        <span
                            class="text-sm font-medium text-slate-800 dark:text-slate-100 font-['Inter'] leading-5"
                        >
                            {{ programChoice.name }}
                        </span>

                        <!-- Check icon when selected -->
                        <div
                            v-if="program.program_id === programChoice.id"
                            style="position: absolute; right: 16px"
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <g clip-path="url(#clip0_26_9297)">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.774 10.1333C16.1237 9.70582 16.0607 9.0758 15.6332 8.72607C15.2058 8.37635 14.5758 8.43935 14.226 8.86679L10.4258 13.5116L9.20711 12.2929C8.81658 11.9024 8.18342 11.9024 7.79289 12.2929C7.40237 12.6834 7.40237 13.3166 7.79289 13.7071L9.79289 15.7071C9.99267 15.9069 10.2676 16.0129 10.5498 15.9988C10.832 15.9847 11.095 15.8519 11.274 15.6333L15.774 10.1333Z"
                                        fill="#FD421D"
                                    />
                                </g>
                                <defs>
                                    <clipPath id="clip0_26_9297">
                                        <rect
                                            width="24"
                                            height="24"
                                            fill="white"
                                        />
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>

                        <!-- Added indicator -->
                        <div
                            v-if="!programChoice.active"
                            class="flex justify-center items-center gap-2 absolute top-5 right-4 z-50"
                            style="opacity: 1 !important"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            <span
                                class="text-slate-800 dark:text-slate-100 font-['Inter'] text-sm font-medium leading-5"
                                @mouseenter="$emit('show-tooltip', $event)"
                                @mouseleave="$emit('hide-tooltip')"
                            >
                                Added
                            </span>
                        </div>
                    </div>
                    <input
                        type="radio"
                        name="program_type"
                        :value="programChoice.id"
                        :checked="program.program_id === programChoice.id"
                        @change="$emit('update:program-id', programChoice.id)"
                        class="sr-only"
                        v-if="programChoice.active"
                    />
                    <div
                        class="border-b border-slate-200 dark:border-slate-700"
                        v-if="index < filteredProgramChoices.length - 1"
                    ></div>
                </label>
            </template>
        </div>
    </div>
</template>

<script setup>
import UiInput from '@/components/ui/inputs/ui-input.vue';

const props = defineProps({
    program: { type: Object, required: true },
    programSearchQuery: { type: String, required: true },
    filteredProgramChoices: { type: Array, required: true },
    canChooseProgramChoice: { type: Boolean, required: true },
    getProgramImageUrl: { type: Function, required: true },
});

defineEmits([
    'update:programSearchQuery',
    'update:program-id',
    'show-tooltip',
    'hide-tooltip',
]);
</script>
